import type { TranslationKeys } from "../types";

/**
 * English translations. Structurally identical to id.ts — the
 * bilingual-parity test fails the build if a key is missing or
 * extra. Tone: enterprise documentation register, matching the
 * standard in apps/web/I18N_GLOSSARY.md.
 */
export const en: TranslationKeys = {
  common: {
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    remove: "Remove",
    loading: "Loading...",
    saving: "Saving...",
    processing: "Processing...",
    error: "An error occurred",
    errorGeneric:
      "An error occurred. Please try again, or contact support if the problem persists.",
    success: "Success",
    confirm: "Confirm",
    close: "Close",
    back: "Back",
    next: "Next",
    retry: "Retry",
    search: "Search",
    filter: "Filter",
    actions: "Actions",
    status: "Status",
    created: "Created",
    updated: "Updated",
    name: "Name",
    description: "Description",
    optional: "Optional",
    required: "Required",
    all: "All",
    none: "None",
    yes: "Yes",
    no: "No",
  },

  auth: {
    brand: {
      eyebrow: "Monago Atrium",
      headline: "One control layer for all AI in your business.",
      subhead:
        "PII redaction, policy enforcement, and audit trail — on every AI call, by default.",
      panelAriaLabel: "Monago Atrium highlights",
      footerCopyright: "© Monago. Part of the Monago platform.",
      visibility: {
        title: "Complete visibility",
        body:
          "Every AI request recorded — who, when, for what, at what cost, which model. Dashboards per team, app, and project.",
      },
      policy: {
        title: "Policy & access control",
        body:
          "Define which teams use which models. Set quotas. Filter sensitive data before it reaches public AI.",
      },
      multiProvider: {
        title: "Multi-provider, no lock-in",
        body:
          "OpenAI, Claude, Gemini, open-source, internal models — one integration. Switch providers without changing code.",
      },
    },
    login: {
      title: "Sign in to Atrium",
      subtitle: "Governance and observability for AI infrastructure",
      emailLabel: "Email",
      emailPlaceholder: "name@company.com",
      passwordLabel: "Password",
      submitButton: "Sign in",
      submitLoading: "Processing...",
      noAccount: "Don't have an account?",
      registerLink: "Register with an invitation code",
      errors: {
        invalidCredentials: "Incorrect email or password.",
        rateLimited:
          "Too many sign-in attempts. Wait a few minutes, then try again.",
        unexpected: "An unexpected error occurred. Please try again.",
      },
    },
    register: {
      haveAccount: "Already have an account?",
      loginLink: "Sign in here",
      step1: {
        title: "Create a new account",
        subtitle: "Step 1 of 3 — Basic information",
        fullNameLabel: "Full name",
        fullNamePlaceholder: "Your full name",
        emailLabel: "Email",
        emailPlaceholder: "name@company.com",
        nextButton: "Continue",
      },
      step2: {
        title: "Invitation code",
        subtitle: "Step 2 of 3 — Enter the signup key from your admin",
        keyLabel: "Signup key",
        keyPlaceholder: "msk_xxxxxxxxxxxxxxxxxx",
        keyHint:
          "Format: msk_ followed by 28 characters. Don't have one? Contact your organisation's admin.",
        backButton: "Back",
        validateButton: "Validate",
        validateLoading: "Validating...",
        errors: {
          invalidKey: "The signup key is invalid or has already been used.",
        },
      },
      step3: {
        title: "Complete registration",
        subtitle: "Step 3 of 3 — Create a password",
        tenantLabel: "Organisation",
        passwordLabel: "Password",
        passwordPlaceholder: "At least {min} characters",
        passwordHintDefault:
          "At least {min} characters — a mix of uppercase, lowercase, digits, and symbols.",
        passwordHintShort: "{remaining} more characters needed.",
        confirmLabel: "Confirm password",
        backButton: "Back",
        submitButton: "Finish",
        submitLoading: "Creating account...",
        errors: {
          tooShort: "The password must be at least {min} characters.",
          mismatch: "The password confirmation does not match.",
          registerFailed:
            "The signup key has already been used, or the password does not meet the security requirements.",
        },
      },
    },
  },

  navigation: {
    groups: {
      main: "Main",
      aiOperations: "AI operations",
      governance: "Governance",
      preferences: "Settings",
      platform: "Platform",
    },
    items: {
      dashboard: "Dashboard",
      apiKeys: "API keys",
      aiProviders: "AI Providers",
      observability: "Observability",
      playground: "Playground",
      policies: "Policies",
      assets: "AI assets",
      risk: "Risk",
      compliance: "Compliance",
      skills: "Skills",
      evaluation: "Evaluation",
      workspaces: "Workspaces",
      settings: "Settings",
      platformTenants: "Tenants",
    },
    sidebar: {
      ariaLabel: "Main navigation",
      expand: "Open sidebar",
      collapse: "Collapse sidebar",
    },
    userMenu: {
      logout: "Sign out",
      language: "Language",
    },
  },

  providers: {
    page: {
      title: "AI Providers",
      description:
        "Manage AI provider credentials per organisation under the BYOK model. Keys are encrypted at rest and shown only once, at creation or rotation.",
      sectionTitle: "AI Providers",
      sectionDescription:
        "Configure API keys per provider (BYOK). Keys are encrypted at rest using industry-standard cryptographic controls; the plaintext is shown only once, at creation or rotation. Every change is recorded in the audit log.",
      readOnlyNotice:
        "Managing AI provider credentials is available to organisation admins only. You can still view the existing credentials below.",
    },
    filters: {
      provider: "Provider",
      status: "Status",
    },
    table: {
      name: "Name",
      provider: "Provider",
      key: "Key",
      status: "Status",
      created: "Created",
      actions: "Actions",
      emptyFiltered: "No credentials match the current filters.",
    },
    status: {
      active: "Active",
      disabled: "Disabled",
      revoked: "Revoked",
    },
    actions: {
      add: "Add provider",
      rotate: "Rotate key",
      refreshModels: "Refresh models",
      refreshModelsSuccess:
        "Model catalog refreshed for “{name}”. Reopen the Playground to see the updated list.",
      disable: "Disable",
      enable: "Enable",
      revoke: "Revoke",
      rowAriaLabel: "Actions for {name}",
    },
    emptyState: {
      title: "No AI provider credentials yet",
      description:
        "Monago uses the BYOK model — you bring your own AI provider API key. Once added, the gateway uses your key for every inference request from this organisation, with no additional Monago charge for upstream calls.",
      step1Title: "Get the API key from the provider dashboard",
      step1Body:
        "OpenAI, Anthropic, or AWS Bedrock. Create a key with read and write access for the models you intend to use.",
      step2Title: "Add the credential in Monago",
      step2Body:
        "Click \"Add provider\". Monago validates the key against the provider endpoint before storing it. The plaintext is encrypted at rest and shown once for you to copy.",
      step3Title: "The gateway uses your key automatically",
      step3Body:
        "Inference requests from this organisation are forwarded to the provider using the credential you added. The audit trail is available in the Observability tab.",
    },
    createDialog: {
      title: "Add an AI provider credential",
      description:
        "Monago validates the key against the provider endpoint before storing it. The key is encrypted at rest and the plaintext is shown only once — have your password manager ready before continuing.",
      providerLabel: "Provider",
      comingSoon: "Coming soon",
      providerHintBedrock:
        "Note: automatic validation for AWS Bedrock will be available in a subsequent release.",
      providerHintDefault:
        "The key is validated against the provider's metadata endpoint.",
      nameLabel: "Credential name",
      namePlaceholder: "e.g. production-openai",
      nameError:
        "Letters, digits, spaces, periods, underscores, and hyphens only (1–200 characters).",
      nameHint:
        "An internal label. You can create several credentials per provider (e.g. 'prod' and 'staging').",
      keyLabel: "API key",
      keyPlaceholder: "sk-…",
      keyError: "The key must be at least 8 characters.",
      keyHint:
        "Taken from your AI provider dashboard. It is not shown again after the credential is created.",
      baseUrlLabel: "Base URL (optional)",
      baseUrlPlaceholder: "https://gateway.example.com",
      baseUrlError: "Must start with http:// or https://.",
      baseUrlHint:
        "Override the endpoint when your AI provider sits behind an internal gateway.",
      providerDetail: "Provider detail: {detail}",
      cancel: "Cancel",
      submit: "Add credential",
      submitLoading: "Validating key…",
    },
    rotateDialog: {
      title: "Rotate API key",
      description:
        "After a successful rotation, the old key no longer works at the gateway. Make sure the new key is valid before continuing.",
      credentialLabel: "Credential",
      currentKeyLabel: "Current key:",
      keyLabel: "New API key",
      keyPlaceholder: "sk-…",
      keyError: "The key must be at least 8 characters.",
      keyHint:
        "Take it from the provider dashboard. It is shown once after a successful rotation.",
      providerDetail: "Provider detail: {detail}",
      cancel: "Cancel",
      submit: "Rotate key",
      submitLoading: "Validating key…",
    },
    revokeDialog: {
      title: "Revoke credential?",
      description:
        "Once revoked, the gateway no longer uses this credential. Inference requests will use the platform default key if one is available, or fail if there is no fallback.",
      warning:
        "Revocation is permanent. The credential row is retained for the audit trail, and there is no undo — create a new credential if needed.",
      targetLabel: "Credential to revoke",
      last4Label: "Last four characters:",
      confirmLabel: "Type \"{name}\" to confirm",
      confirmHint:
        "The name must match exactly (case-sensitive) to enable the revoke button.",
      cancel: "Cancel",
      submit: "Revoke credential",
      submitLoading: "Revoking…",
    },
    showOnceDialog: {
      headlineCreated: "API key created",
      headlineRotated: "API key rotated",
      description:
        "Save the API key below now. Once the dialog is closed, Monago does not store the plaintext and cannot show it again.",
      copyAria: "Copy API key",
      copiedAria: "Key copied",
      copy: "Copy",
      copied: "Copied",
      warningTitle: "Important:",
      warningBody:
        "This key is shown only once. Save it in a password manager or secret vault before closing the dialog. Monago stores the key in encrypted form on the server; the plaintext is not written to the audit log.",
      last4Label: "Last four characters:",
      createdLabel: "Created:",
      acknowledge:
        "I have copied the API key and stored it in a secure location.",
      close: "Saved, close",
    },
  },
  workspaces: {
    page: {
      title: "Workspaces",
      description:
        "{active} of {max} workspaces active. Workspaces isolate policies, asset inventory, and API keys per environment or team.",
      readOnlyNotice:
        "Managing workspaces — creating, archiving, and setting the default — is available to organisation admins only. You can still open the detail of any workspace where you are a member.",
      limitReached:
        "The limit of {max} active workspaces has been reached. Archive a workspace you no longer use before adding a new one.",
      sectionTitle: "All workspaces",
      sectionDescription:
        "Includes archived workspaces. They are hidden from the workspace switcher but retained for audit purposes.",
    },
    table: {
      name: "Name",
      slug: "Slug",
      status: "Status",
      default: "Default",
      members: "Members",
      created: "Created",
      actions: "Actions",
      empty:
        "No workspaces yet. The organisation default workspace is created during onboarding. If it does not appear, contact the Monago team.",
    },
    status: {
      active: "Active",
      archived: "Archived",
    },
    roles: {
      workspace_admin: "Workspace Admin",
      workspace_member: "Member",
      workspace_viewer: "Viewer",
    },
    badges: {
      isDefault: "Default",
      defaultYes: "Yes",
      defaultNo: "No",
    },
    actions: {
      create: "Create workspace",
      openDetail: "Open detail",
      setDefault: "Set as default",
      defaultActive: "Default active",
      archive: "Archive",
      rowAriaLabel: "Actions for {name}",
    },
    detail: {
      backToList: "Back to list",
      backToWorkspaces: "Back to workspace list",
      noDescription: "No description.",
      setDefault: "Set as default",
      archive: "Archive",
      metadataTitle: "Metadata",
      metadataDescription:
        "Core workspace identity. The slug and organisation ID are permanent to preserve data lineage.",
      fieldSlug: "Slug",
      fieldStatus: "Status",
      fieldDefault: "Default workspace",
      fieldMemberCount: "Member count",
      fieldCreated: "Created",
      fieldUpdated: "Updated",
      membersTitle: "Workspace members",
      membersDescription:
        "Organisation admins can always manage members. A workspace_admin can manage members for this workspace only.",
      addMember: "Add member",
      memberName: "Name",
      memberEmail: "Email",
      memberRole: "Role",
      memberJoined: "Joined",
      memberActions: "Actions",
      membersEmpty:
        "No members yet. Select \"Add member\" to start adding workspace members.",
      removeMember: "Remove",
      removeMemberAriaLabel: "Remove {email} from the workspace",
    },
    addModal: {
      title: "Create a new workspace",
      description:
        "Workspaces isolate policies, asset inventory, and API keys per environment. Members of your organisation can be assigned as workspace admins, members, or viewers.",
      nameLabel: "Workspace name",
      namePlaceholder: "e.g. Production, Staging, Treasury",
      nameError: "Required, between 1 and 200 characters.",
      slugLabel: "Slug",
      slugPlaceholder: "production",
      slugError:
        "Lowercase letters, digits, and hyphens only, between 1 and 100 characters.",
      slugHint:
        "Generated automatically from the name and can be adjusted. Used as the identifier in the URL and database.",
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "Context for how this workspace is used",
      cancel: "Cancel",
      submit: "Create workspace",
      submitLoading: "Creating…",
    },
    archiveDialog: {
      title: "Archive workspace?",
      description:
        "An archived workspace no longer appears in the workspace switcher and does not accept new requests. Its history and audit log are retained with integrity preserved.",
      workspaceLabel: "Workspace",
      slugLabel: "Slug:",
      defaultSuffix: "· default",
      confirmLabel: "Type \"{name}\" to confirm",
      confirmHint:
        "The name must match exactly, including letter case, to enable the archive button.",
      cancel: "Cancel",
      submit: "Archive workspace",
      submitLoading: "Archiving…",
      blockedDefault:
        "The default workspace cannot be archived. Set another workspace as the default first.",
      blockedLastActive:
        "At least one active workspace must remain. Create another workspace first.",
    },
    addMemberDialog: {
      title: "Add workspace member",
      description:
        "Select a member of your organisation and assign a workspace role. Only active users who are not already part of this workspace are shown.",
      searchLabel: "Search users",
      searchPlaceholder: "Name or email",
      loadingUsers: "Loading users…",
      noCandidates:
        "No candidates. Every organisation member has already joined, or none match the search term.",
      roleLabel: "Workspace role",
      roleGroupAriaLabel: "Role",
      roleHintAdmin: "Manages members and edits workspace metadata.",
      roleHintMember:
        "Standard day-to-day access to workspace policies and assets.",
      roleHintViewer:
        "Read-only access, suitable for auditors or external observers.",
      cancel: "Cancel",
      submit: "Add member",
      submitLoading: "Adding…",
    },
    selector: {
      loading: "Loading workspaces…",
      loadingAriaLabel: "Loading workspaces",
      createFirst: "Create workspace",
      createFirstAriaLabel: "Create the first workspace",
      emptyNonAdmin: "No workspaces yet",
      emptyCompact: "—",
      placeholder: "Select a workspace",
      triggerAriaLabel: "Workspace: {name}",
      triggerUnselected: "not selected",
      defaultSuffix: "· default",
      createNew: "Create a new workspace",
      manage: "Manage workspaces",
    },
  },
  policies: {
    page: {
      title: "Policies",
      description:
        "Governance rules evaluated against every inference request. Each policy is versioned — changes are made as a new version.",
      breadcrumbLoading: "Loading…",
      notFound:
        "Policy not found. It may have been deleted, or you may not have access to it.",
      missingId: "No policy identifier was found in this address.",
    },
    table: {
      name: "Name",
      slug: "Slug",
      status: "Status",
      priority: "Priority",
      updated: "Last updated",
      actions: "Actions",
      emptyFiltered:
        "No policies match the current filter. Adjust the filter to show other policies.",
    },
    status: {
      enabled: "Active",
      disabled: "Disabled",
    },
    effects: {
      allow: "Allow",
      deny: "Deny",
      logOnly: "Log only",
      redact: "Redact sensitive data",
      requireApproval: "Requires approval",
      requireHitl: "Requires human review",
      escalate: "Escalate",
    },
    actions: {
      create: "Create policy",
      newVersion: "New version",
      delete: "Delete",
      deleteRowAriaLabel: "Delete policy {name}",
    },
    detail: {
      decisionsTitle: "Recent decisions",
      decisionsDescription: "The 20 most recent evaluations of this policy.",
      decisionsEmpty:
        "No decisions recorded for this policy yet. Decisions appear once inference requests have been evaluated against it.",
      colTime: "Time",
      colEffect: "Effect",
      colRequestId: "Request ID",
      colUser: "User",
      systemActor: "system",
      metadataSlug: "Slug",
      metadataPriority: "Priority",
      metadataStatus: "Status",
      metadataUpdated: "Last updated",
    },
    createDialog: {
      title: "Create a new policy",
      description:
        "Pick model access control for a quick allowlist, or advanced for full DSL editing. The first version automatically becomes the active version once created.",
      modeAria: "Policy authoring mode",
      modeModelAccess: "Model access control",
      modeAdvanced: "Advanced (DSL)",
      templateLabel: "Template (optional)",
      templateNone: "— Create custom (no template) —",
      templateError: "Failed to load templates: {detail}",
      nameLabel: "Name",
      namePlaceholder: "e.g. PII redaction",
      slugLabel: "Slug",
      slugPlaceholder: "pii-redaction",
      slugError: "Lowercase letters, digits, and hyphens only.",
      slugHint: "Generated automatically from the name; can be edited manually.",
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "A short explanation of the policy's purpose",
      priorityLabel: "Priority",
      priorityHint: "0 = lowest, 100 = highest. Defaults to 50.",
      ruleLabel: "Rule definition (JSON)",
      ruleHint:
        "Required: name · when · effect. Optional: priority · description · framework_mapping",
      cancel: "Cancel",
      submit: "Create policy",
      submitLoading: "Creating…",
    },
    modelAccess: {
      intro: "Allowlist of models permitted in this scope.",
      introDetail:
        "Requests for any other model are denied at the policy engine. Existing audit + decision history are preserved.",
      scopeLabel: "Scope",
      scopeWorkspace: "This workspace",
      scopeTenant: "Entire tenant",
      scopeNoWorkspaceHint:
        "You are not in an active workspace — tenant scope will be used.",
      modelsLabel: "Allowed models",
      modelsHint: "{count} model(s) selected.",
      modelsRequiredError: "At least one model must be selected.",
      searchPlaceholder: "Search models…",
      selectAll: "Select all",
      selectFiltered: "Select {count} match(es)",
      clear: "Clear",
      removeModel: "Remove {model}",
      searchEmpty: "No models match “{query}”.",
      catalogError: "Failed to load the model catalog: {detail}",
      catalogEmpty:
        "No dispatchable models yet — connect a provider on the Providers page first.",
      denyMessage:
        "This model is not permitted by your workspace policy.",
    },
    deleteDialog: {
      title: "Delete policy",
      descriptionBefore: "This action deletes all versions of the policy ",
      descriptionMiddle: " and ",
      descriptionIrreversible: "cannot be undone",
      descriptionAfter:
        ". Decisions already recorded in the audit log are retained.",
      confirmInstruction: "To confirm, type the policy name exactly as shown:",
      confirmLabel: "Confirm name",
      confirmError: "Name does not match.",
      cancel: "Cancel",
      submit: "Delete permanently",
      submitLoading: "Deleting…",
    },
    versionDialog: {
      title: "New version for {name}",
      description:
        "The new version automatically becomes the active version once created. The previous version is archived and retained for audit purposes.",
      ruleLabel: "Rule definition (JSON)",
      ruleHint: "Required: name · when · effect.",
      changelogLabel: "Changelog (optional)",
      changelogPlaceholder: "e.g. add NPWP field to the redaction list",
      changelogHint: "An internal note attached to this version.",
      cancel: "Cancel",
      submit: "Create & activate version",
      submitLoading: "Processing…",
    },
    quickstart: {
      title: "No policies yet",
      description:
        "A policy is a governance rule run against every inference request. Follow the three steps below to get started.",
      step1Title: "Choose a BFSI template or create a custom one",
      step1Body:
        "Six ready-to-use BFSI templates are available in the dropdown when you select \"Create policy\" — including external LLM restriction for finance/treasury, HR-data local-model-only, and others. Each template already includes compliance framework mapping to ISO 42001, UU PDP 2022, and POJK 11/2022. Example rule_definition contents:",
      step2Title: "Customise for your organisation's context",
      step2Body:
        "Once a template is loaded into the editor, change the name, deny_user_message, and when conditions to match your workspace and internal teams. The slug is generated automatically from the name; the priority field (0–100) determines the evaluation order against other rules.",
      step3Title: "Publish — the first version is active automatically",
      step3Body:
        "Each policy can have many versions; the first version is active immediately once successfully created. For later changes, create a new version from the detail page — older versions are archived for audit and rollback.",
    },
  },
  assets: {
    page: {
      title: "Asset inventory",
      description:
        "A central registry for AI assets: prompts, models, agents, workflows, providers, tools, and datasets. Every asset has a risk classification and a lifecycle stage.",
      breadcrumbLoading: "Loading…",
      notFound:
        "Asset not found. It may have been archived, or you may not have access to it.",
      missingId: "No asset identifier was found in this address.",
    },
    table: {
      name: "Name",
      type: "Type",
      lifecycle: "Lifecycle",
      risk: "Risk",
      tags: "Tags",
      updated: "Last updated",
      actions: "Actions",
      emptyFiltered:
        "No assets match the current filter. Adjust the filter to show other assets.",
    },
    status: {
      draft: "Draft",
      review: "Review",
      active: "Active",
      deprecated: "Deprecated",
      retired: "Retired",
    },
    types: {
      prompt: "Prompt",
      model: "Model",
      agent: "Agent",
      workflow: "Workflow",
      provider: "Provider",
      tool: "Tool",
      dataset: "Dataset",
    },
    risk: {
      low: "Low",
      medium: "Medium",
      high: "High",
      critical: "Critical",
    },
    actions: {
      create: "Register asset",
      transition: "Move stage",
      delete: "Delete",
      deleteRowAriaLabel: "Delete asset {name}",
    },
    detail: {
      fieldType: "Type",
      fieldLifecycle: "Lifecycle",
      fieldRisk: "Risk",
      fieldUpdated: "Last updated",
      fieldSlug: "Slug",
      fieldTags: "Tags",
      fieldCoOwners: "Co-owners",
      fieldDependencies: "Dependencies",
      coOwnersCount: "{count} users",
      dependenciesCount: "{out} out · {in} in",
      historyTitle: "Change history",
      historyDescription: "The 20 most recent events for this asset.",
      historyColTime: "Time",
      historyColChangeType: "Change type",
      historyColChangedBy: "Changed by",
      historyEmpty:
        "No changes recorded for this asset yet. Changes appear once the asset is edited or its stage is moved.",
      systemActor: "system",
    },
    createDialog: {
      title: "Register a new asset",
      description:
        "Choose an asset type, give it a name, and set an initial risk classification. The lifecycle stage defaults to Draft and can be changed from the detail page.",
      typeLabel: "Asset type",
      nameLabel: "Name",
      namePlaceholder: "e.g. {type} production",
      slugLabel: "Slug",
      slugHint: "Generated automatically from the name; can be edited manually.",
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "A short explanation of the asset",
      riskLabel: "Risk classification",
      justificationLabel: "Business justification (optional)",
      justificationPlaceholder:
        "The business purpose behind registering this asset",
      tagsLabel: "Tags (optional, comma-separated)",
      tagsPlaceholder: "pii, retail, kyc",
      tagsHint: "Tags help filter the asset list and support analytics.",
      cancel: "Cancel",
      submit: "Register asset",
      submitLoading: "Registering…",
    },
    deleteDialog: {
      title: "Delete asset",
      descriptionBefore: "The asset ",
      descriptionAfter:
        " will be archived. Its history is retained for the audit trail, but the asset will no longer appear in the asset list.",
      confirmInstruction: "To confirm, type the asset name exactly as shown:",
      confirmLabel: "Confirm name",
      confirmError: "Name does not match.",
      cancel: "Cancel",
      submit: "Delete asset",
      submitLoading: "Archiving…",
    },
    transitionDialog: {
      title: "Move lifecycle stage",
      descriptionBefore: "The asset ",
      descriptionMiddle: " is currently at the ",
      descriptionAfter:
        " stage. Choose a target stage and, if needed, record a reason.",
      targetLabel: "Target stage",
      reasonLabel: "Reason (optional)",
      reasonPlaceholder: "e.g. review complete, ready for production",
      reasonHint: "This note is saved to the asset's change history.",
      cancel: "Cancel",
      submit: "Move stage",
      submitLoading: "Processing…",
    },
    filterBar: {
      type: "Type",
      lifecycle: "Lifecycle",
      risk: "Risk",
    },
    quickstart: {
      title: "No assets registered yet",
      description:
        "The asset inventory is a central registry for AI assets: prompts, models, agents, workflows, providers, tools, and datasets. Every asset has a risk classification and a lifecycle stage.",
      step1Title: "Register your first asset",
      step1Body:
        "Choose an asset type — prompt, model, agent, workflow, provider, tool, or dataset — then give it a name and set an initial risk classification.",
      step2Title: "Manage it through the lifecycle stages",
      step2Body:
        "Assets move through the draft, review, active, deprecated, and retired stages. Use the \"Move stage\" button on the detail page to change the stage.",
      step3Title: "Track changes through the history",
      step3Body:
        "Every change — a rename, a risk re-classification, or a stage move — is recorded in the change history on the detail page.",
    },
  },
  risk: {
    page: {
      title: "Risk posture",
      description:
        "The AI Governance risk score for your organization — an aggregate of four dimensions (security, policy, asset, and compliance) with signal evidence and framework alignment.",
    },
    tabs: {
      dashboard: "Dashboard",
      snapshots: "Snapshots",
      signals: "Signals",
      methodology: "Methodology",
    },
    bands: {
      critical: "Critical",
      needsAttention: "Needs attention",
      good: "Good",
      excellent: "Excellent",
    },
    severity: {
      low: "Low",
      medium: "Medium",
      high: "High",
      critical: "Critical",
    },
    dimensions: {
      security: "Security",
      policy: "Policy",
      asset: "Asset",
      compliance: "Compliance",
    },
    dashboard: {
      intro:
        "The overall risk score is the weighted average of four dimensions: security, policy, asset, and compliance.",
      recompute: "Recompute",
      recomputeLoading: "Processing…",
      overallScoreLabel: "Overall score",
      deltaLabel: "Change vs previous snapshot",
      maturityLabel: "NIST AI RMF maturity",
      computedAt: "Computed {time}",
      dimensionsTitle: "Risk dimensions",
      dimensionsDescription:
        "The four sub-scores that make up the overall score.",
      dimensionsColDimension: "Dimension",
      dimensionsColScore: "Score",
      dimensionsColDelta: "Change",
      dimensionsEmpty:
        "No dimensions have been computed yet. Run a recompute from the Dashboard tab to produce the first score.",
      frameworksTitle: "Framework alignment",
      frameworksDescription:
        "The readiness score for each framework active on this organization.",
      frameworksColFramework: "Framework",
      frameworksColReadiness: "Readiness score",
      frameworksColMaturity: "Maturity",
      frameworksColComputed: "Computed",
      frameworksEmpty:
        "No framework alignment has been computed yet. Alignment appears once the risk score is updated.",
    },
    snapshots: {
      colTime: "Time",
      colOverall: "Overall score",
      colDelta: "Change",
      empty:
        "No snapshots yet. Run a recompute from the Dashboard tab to create the first snapshot.",
    },
    snapshotDetail: {
      title: "Risk snapshot detail",
      subtitle: "Computed {time} · type {type}",
      overallScoreLabel: "Overall score",
      deltaLabel: "Change vs previous",
      evidenceLabel: "Signal evidence",
      evidenceHint:
        "The signal count per dimension used when computing the aggregate.",
      close: "Close",
    },
    signals: {
      filterSeverity: "Severity",
      filterDimension: "Dimension",
      colTime: "Time",
      colType: "Type",
      colSeverity: "Severity",
      colDimension: "Dimension",
      colSource: "Source",
      colValue: "Value",
      empty:
        "No signals match the current filter. Adjust the filter to show other signals.",
    },
    signalDetail: {
      title: "Signal detail",
      subtitle: "{type} · {time}",
      severityLabel: "Severity",
      dimensionLabel: "Affected dimension",
      sourceLabel: "Source",
      valueLabel: "Signal value",
      triggeredLabel: "Triggered a recompute",
      triggeredYes: "Yes",
      triggeredNo: "No",
      snapshotLabel: "Related snapshot",
      metadataLabel: "Event metadata",
      close: "Close",
    },
    methodology: {
      weightsTitle: "Dimension weights",
      weightsDescription:
        "The weights must total 1.00. Each sub-score is multiplied by its weight and summed into the overall score.",
      edit: "Edit methodology",
      weightSecurity: "Security",
      weightPolicy: "Policy",
      weightAsset: "Asset",
      weightCompliance: "Compliance",
      weightSumLabel: "Total weight: {sum}",
      weightSumValid: "(valid)",
      weightSumInvalid: "(must be 1.00 to save)",
      thresholdsTitle: "Score band thresholds",
      thresholdsDescription:
        "A score below the \"Critical\" threshold is marked Critical; below the \"Needs attention\" threshold, Needs attention; below the \"Good\" threshold, Good; at or above the \"Good\" threshold, Excellent.",
      thresholdCritical: "Critical (<)",
      thresholdNeedsAttention: "Needs attention (<)",
      thresholdGood: "Good (<)",
      thresholdsOrderError:
        "Thresholds must increase in order: Critical lower than Needs attention, and Needs attention lower than Good.",
      thresholdsRangeError:
        "Each threshold must be within the 0 to 100 range.",
      cancel: "Cancel",
      save: "Save methodology",
      saveLoading: "Saving…",
      readOnlyNotice:
        "Only organization admins can change the methodology configuration.",
    },
  },
  compliance: {
    page: {
      title: "Compliance",
      description:
        "Manage compliance on a self-service basis: subscribe to frameworks, review gap analysis, manage evidence, and track NIST AI RMF 1.0 and ISO/IEC 42001:2023 maturity.",
    },
    tabs: {
      posture: "Posture",
      frameworks: "Frameworks",
      gaps: "Gap analysis",
      evidence: "Evidence",
      maturity: "Maturity",
    },
    posture: {
      empty:
        "No frameworks are subscribed yet. Open the \"Frameworks\" tab to subscribe to a relevant framework, for example NIST AI RMF 1.0 or ISO/IEC 42001:2023.",
      cardFramework: "Framework",
      readinessLabel: "Readiness score",
      coveredClauses: "Clauses covered",
      criticalClauses: "Critical clauses",
      gapNotice:
        "{count} clauses do not yet have evidence — review the \"Gap analysis\" tab to follow up.",
    },
    frameworks: {
      intro:
        "The frameworks this organization subscribes to. Each framework triggers a periodic recomputation of readiness and maturity.",
      colFramework: "Framework",
      colStatus: "Status",
      colTargetCertification: "Target certification",
      colSince: "Since",
      colActions: "Actions",
      statusActive: "Active",
      statusInactive: "Inactive",
      empty:
        "No framework subscriptions yet. Choose \"Subscribe to a framework\" to start tracking readiness.",
    },
    gaps: {
      frameworkLabel: "Framework",
      noActiveFramework: "— No active framework —",
      gapCount: "{count} clauses do not yet have evidence",
      colClauseCode: "Clause code",
      colTitle: "Title",
      colCategory: "Category",
      colPriority: "Priority",
      noFramework:
        "No frameworks are subscribed yet. Subscribe to a framework first on the \"Frameworks\" tab.",
      noGaps:
        "No gaps — every clause already has evidence.",
      note:
        "Note: clauses marked \"Critical\" priority should be addressed first so the readiness score improves more quickly.",
    },
    evidence: {
      intro:
        "Evidence links a policy, asset, or risk snapshot to a framework clause. Automatic mapping is run by the system; organization admins can add manual evidence or revoke evidence that no longer applies.",
      typeFilterLabel: "Type",
      includeRevoked: "Show evidence that has been revoked",
      colClause: "Clause",
      colType: "Type",
      colReference: "Reference",
      colCreated: "Created",
      colStatus: "Status",
      colActions: "Actions",
      statusActive: "Active",
      statusRevoked: "Revoked",
      empty:
        "No evidence matches the current filter. Adjust the filter or add new evidence.",
    },
    maturity: {
      nistTitle: "NIST AI RMF 1.0",
      nistDescription:
        "Maturity level per core function: GOVERN, MAP, MEASURE, and MANAGE.",
      isoTitle: "ISO/IEC 42001:2023",
      isoDescription:
        "Maturity level per cycle phase: PLAN, DO, CHECK, and ACT.",
      overallLabel: "Overall maturity",
      functionLabel: "Function",
      phaseLabel: "Phase",
      coverage: "{pct}% coverage",
    },
    priority: {
      critical: "Critical",
      high: "High",
      normal: "Normal",
    },
    evidenceType: {
      policy_version: "Policy version",
      asset: "Asset",
      risk_snapshot: "Risk snapshot",
      audit_log: "Audit log",
      framework_alignment: "Framework alignment",
    },
    actions: {
      subscribe: "Subscribe to a framework",
      unsubscribe: "Unsubscribe",
      submitEvidence: "Submit evidence",
      revokeEvidence: "Revoke",
    },
    subscribeDialog: {
      title: "Subscribe to a framework",
      description:
        "Enable readiness tracking for an additional framework. The system will compute the score and automatically link evidence from existing policies and assets.",
      frameworkLabel: "Framework",
      frameworkPlaceholder: "— Select a framework —",
      allSubscribed:
        "All available frameworks are already subscribed.",
      targetDateLabel: "Target certification (optional)",
      targetDateHint:
        "A target date used as a reference for the internal audit roadmap.",
      cancel: "Cancel",
      submit: "Subscribe",
      submitLoading: "Processing…",
    },
    evidenceSubmitDialog: {
      title: "Submit manual evidence",
      description:
        "Link a policy version, asset, risk snapshot, or audit log to a specific framework clause to close a gap. This action can be undone by revoking the evidence later.",
      clauseIdLabel: "Clause ID",
      clauseIdPlaceholder: "Clause ID — see the \"Gap analysis\" tab",
      typeLabel: "Evidence type",
      referenceIdLabel: "Reference ID",
      referenceIdPlaceholder:
        "Resource ID, for example a policy version ID",
      referenceIdHint:
        "The ID of the resource used as evidence.",
      expiresAtLabel: "Valid until (optional)",
      expiresAtHint:
        "Leave blank if the evidence has no expiry.",
      cancel: "Cancel",
      submit: "Submit evidence",
      submitLoading: "Saving…",
    },
  },
  observability: {
    page: {
      title: "Observability",
      description:
        "Monitor your organization's governance posture, inference metrics, policy decisions, and audit trail.",
    },
    tabs: {
      overview: "Overview",
      inference: "Inference",
      policy: "Policy",
      audit: "Audit log",
    },
    overview: {
      tilePostureLabel: "Governance posture",
      tilePostureNoData: "No data",
      tileRequestsLabel: "Requests, 24 hours",
      tileRequestsP99: "p99 {value}",
      tileRequestsNoData: "No data yet",
      tileDecisionsLabel: "Policy decisions (7 days)",
      tileDecisionsDenied: "{count} denied",
      tileAuditLabel: "Recent audit events",
      tileAuditCaption: "Open the Audit tab for details",
      concernsTitle: "Risk concentration",
      concernsDescription:
        "Top concerns from the governance posture report.",
      concernsEmpty:
        "No concerns reported yet. Your organization's governance posture is in good shape for now.",
      recentAuditTitle: "Recent audit activity",
      recentAuditDescription:
        "The five most recent events in your organization.",
      recentAuditEmpty:
        "No audit events yet. Activity will appear here after the first request passes through the gateway.",
    },
    inference: {
      intro:
        "End-to-end latency metrics with a breakdown by gateway stage.",
      tileP50: "p50",
      tileP95: "p95",
      tileP99: "p99",
      tileTotal: "Total",
      stagesTitle: "Latency per gateway stage",
      stagesDescription:
        "Authentication, PII scanning, policy evaluation, provider call, and audit logging.",
      stagesColStage: "Stage",
      stagesColP50: "p50",
      stagesColP95: "p95",
      stagesColP99: "p99",
      stagesColCount: "Count",
      stagesEmpty:
        "No per-stage data for this time range. Try selecting a longer time range.",
      stagesGroupGateway: "Gateway (governance)",
      stagesGroupProvider: "Provider (LLM)",
      stagesGroupOther: "Other",
      topModelsTitle: "Top models",
      topModelsDescription:
        "The models called most often in this time range.",
      topModelsColModel: "Model",
      topModelsColRequests: "Requests",
      topModelsColTokens: "Tokens",
      topModelsColCostUsd: "Cost (USD)",
      topModelsColCostIdr: "Cost (IDR)",
      topModelsColAvgLatency: "Average latency",
      topModelsEmpty:
        "No model calls for this time range. Try selecting a longer time range.",
      costTitle: "Estimated cost (informational)",
      costDescription:
        "Estimated total cost for this time range. Tokens are billed directly by your provider; IDR is a conversion at your tenant FX rate, for reporting only.",
      costTileTotalUsd: "Total (USD)",
      costTileTotalIdr: "Total (IDR)",
      costTileFxRate: "FX rate",
      costFxCaption: "@ Rp {{rate}} / USD",
      costPricingSourceLabel: "Pricing source",
      costPricingFallbackBadge: "Estimated (fallback)",
      costEmpty:
        "No cost data for this time range. Try selecting a longer time range.",
    },
    policy: {
      intro:
        "Policy engine decision statistics with the most frequently triggered policies.",
      totalLabel: "Total decisions",
      breakdownTitle: "Breakdown by effect",
      breakdownDescription:
        "The full distribution of all effects in this time range.",
      breakdownColEffect: "Effect",
      breakdownColCount: "Count",
      breakdownColPercentage: "Percentage",
      breakdownEmpty:
        "No decisions recorded for this time range yet.",
      topTitle: "Most frequently triggered policies",
      topDescription:
        "The ten most active policies in this time range.",
      topColName: "Policy name",
      topColHits: "Hits",
      topColEffect: "Effect",
      topEmpty:
        "No policies triggered for this time range yet.",
    },
    audit: {
      filterStatusAria: "Filter by status",
      filterStatusAll: "All statuses",
      filterPiiAria: "Filter by PII",
      filterPiiAll: "Any PII",
      filterPiiDetected: "PII detected",
      filterPiiNone: "No PII",
      filterActionAria: "Filter by action",
      filterActionPlaceholder: "Action (for example api_key.create)",
      reset: "Reset",
      colTime: "Time",
      colAction: "Action",
      colStatus: "Status",
      colPath: "Path",
      colLatency: "Latency",
      colPii: "PII",
      latencyValue: "{ms} ms",
      piiPresent: "Yes",
      emptyFiltered:
        "No audit events match the filter. Adjust the filter to show other events.",
      empty:
        "No audit events for this organization yet. Activity will be recorded here after the first request passes through the gateway.",
    },
    auditStatus: {
      success: "Succeeded",
      blocked: "Blocked",
      error: "Failed",
    },
    effects: {
      allow: "Allow",
      deny: "Deny",
      require_hitl: "Human review",
      require_approval: "Approval",
      redact: "Redact",
      log_only: "Log only",
      escalate: "Escalate",
    },
    windows: {
      "1h": "1 hour",
      "24h": "24 hours",
      "7d": "7 days",
      "30d": "30 days",
    },
  },
  settings: {
    page: {
      title: "Settings",
      description:
        "Manage your user profile, organisation metadata, and organisation members from a single place.",
      tabProfileLabel: "User profile",
      tabProfileDescription: "Identity and preferences",
      tabTenantLabel: "Organisation",
      tabTenantDescription: "Organisation name and contact",
      tabUsersLabel: "Users & invitations",
      tabUsersDescription: "Members and invitation codes",
      tabFxRateLabel: "USD → IDR rate",
      tabFxRateDescription: "FX rate for cost reporting",
      tabAllowedOriginsLabel: "Allowed origins",
      tabAllowedOriginsDescription: "Browser origins permitted via CORS",
      tabDataProtectionLabel: "Data Protection",
      tabDataProtectionDescription: "PII and prompt-injection actions",
    },
    roles: {
      super_admin: "Super admin",
      admin: "Admin",
      member: "Member",
      viewer: "Viewer",
      partner_admin: "Partner admin",
    },
    userStatus: {
      active: "Active",
      suspended: "Suspended",
      deleted: "Deleted",
    },
    profile: {
      identity: {
        title: "Identity",
        description:
          "These account details are taken from your login session and cannot be changed directly from this page.",
        emptyValue: "—",
        roleLabel: "Role",
        tenantIdLabel: "Organisation ID",
        workspaceLabel: "Active workspace",
      },
      password: {
        title: "Change password",
        description:
          "A password reset link is sent to your email. Passwords are not changed directly in the interface so that every change is recorded and account guessing is not made easier.",
        intro:
          "Click the button below to receive an email with a password reset link. Your current session stays active; the new password takes effect once you open the link from the email.",
        sentNotice:
          "If the email {email} is registered, a password reset link has been sent. Check your inbox — the link is valid for a short time.",
        sendButton: "Send password reset email",
        sendButtonLoading: "Sending...",
        resendButton: "Resend",
        resendAria: "Resend the password reset request",
      },
      language: {
        title: "Language preference",
        description:
          "Interface language. This preference is stored in this browser only — server synchronisation will be available in a future release.",
        groupAria: "Interface language",
        idLabel: "Bahasa Indonesia",
        idDescription:
          "Default — all copy and date formats follow the Indonesian standard.",
        enLabel: "English",
        enDescription:
          "Experimental — some copy is still in Indonesian and not yet translated.",
      },
    },
    tenant: {
      readOnlyNotice:
        "Only organisation admins can change these settings. You can still view the metadata below.",
      savedNotice:
        "Changes saved {time}. The audit record keeps a history of who changed what.",
      identity: {
        title: "Organisation identity",
        description:
          "The slug and ID are permanent for data lineage. The display name and contact email can be changed by an admin.",
        nameLabel: "Display name",
        nameError: "Required (1–200 characters).",
        nameHint:
          "The name shown on dashboard pages and in organisation emails.",
        emailLabel: "Contact email",
        emailError: "Invalid email format.",
        emailHint:
          "Optional. Used for organisation notifications and internal billing.",
        emailPlaceholder: "contact@company.co.id",
        tenantIdLabel: "Organisation ID",
        slugLabel: "Slug (immutable)",
        resetButton: "Reset",
        saveButton: "Save changes",
        saveButtonLoading: "Saving...",
      },
      lifecycle: {
        title: "Status & lifecycle",
        description:
          "Subscription status and the organisation's creation time. These fields are set by the Monago team and cannot be changed here.",
        statusLabel: "Status",
        statusActive: "Active",
        statusInactive: "Inactive",
        createdLabel: "Created",
        updatedLabel: "Updated",
        emptyValue: "—",
      },
    },
    fxRate: {
      readOnlyNotice:
        "Only organisation admins can change the USD → IDR rate. Other users see the rate read-only.",
      savedNotice:
        "FX rate saved {time}. Future cost displays use the new rate.",
      governanceCaption:
        "For cost reporting only. Tokens are billed directly by your provider — Monago does not bill or take margin.",
      section: {
        title: "USD → IDR exchange rate",
        description:
          "The rate used to convert provider cost (in USD) to IDR on the observability dashboard. Set your treasury reference; existing telemetry is not retroactively recomputed.",
        currentLabel: "Current rate",
        platformDefault: "Platform default",
        tenantCustom: "Custom tenant rate",
        inputLabel: "New rate (Rp / USD)",
        inputHint:
          "Between Rp 1.000 and Rp 100.000 per USD. Use whole rupiah or up to two decimals.",
        inputError: "Enter a positive number between 1.000 and 100.000.",
        reasonLabel: "Reason (optional)",
        reasonHint:
          "Recorded in the audit trail. Useful for treasury or finance review.",
        reasonPlaceholder: "e.g. Treasury reference 2026-05",
        resetButton: "Reset",
        saveButton: "Save rate",
        saveButtonLoading: "Saving...",
      },
    },
    allowedOrigins: {
      intro:
        "Register browser origins that your web apps will call this gateway from. CORS preflight accepts requests from this list plus the Monago system origins.",
      readOnlyNotice:
        "Only organisation admins can change allowed origins.",
      empty:
        "No custom origins yet. Browser apps calling the gateway must be added here before their CORS preflight will succeed.",
      removeButton: "Remove",
      removeAria: "Remove {origin}",
      removeConfirm:
        "Remove {origin}? Browser apps using this origin will be blocked immediately.",
      conflictMessage:
        "Origin {origin} is already registered (by another tenant or the Monago system list). Pick a different host.",
      section: {
        title: "Allowed browser origins",
        description:
          "Each entry is scheme://host[:port] — no path, no wildcard. Production must use https://; localhost is allowed for development.",
        inputLabel: "New origin",
        inputPlaceholder: "https://app.example.com",
        inputHint:
          "Examples: https://app.example.com, https://acme.id, http://localhost:3000.",
        addButton: "Add origin",
        addButtonLoading: "Adding...",
      },
      errors: {
        empty: "Origin must not be empty.",
        wildcard: "Wildcard origins (*) are not allowed.",
        whitespace: "Origin must not contain whitespace.",
        format: "Must be a valid URL of the form scheme://host[:port].",
        scheme: "Only http:// and https:// are supported.",
        path: "Origin must not contain a path.",
        query: "Origin must not contain a query or fragment.",
        userinfo: "Origin must not contain userinfo (user:password@…).",
        https: "Production origins must use https:// (localhost is exempt).",
        duplicate: "Origin is already in the list.",
      },
    },
    dataProtection: {
      overrideNotice:
        "Default protection policy. Workspace or user policies may override these settings — this editor only touches the tenant-wide default.",
      firstSaveNotice:
        "No tenant-default policy exists yet. The first save creates one with the values below.",
      readOnlyNotice:
        "Only organisation admins can change protection settings.",
      savedNotice:
        "Saved {time}. New requests use the updated enforcement.",
      resetButton: "Reset",
      saveButton: "Save",
      saveButtonLoading: "Saving...",
      pii: {
        title: "PII handling",
        description:
          "What the gateway does when the PII detector flags content. Detection always runs; this controls the action.",
        options: {
          block: {
            label: "Block",
            description:
              "Refuse the request before it reaches the provider. Logged in the audit chain. Strictest enforcement.",
          },
          full_mask: {
            label: "Full Mask",
            description:
              "Replace PII with [REDACTED_<type>] — zero-tail, no digits survive. Strictest masking.",
          },
          partial_mask: {
            label: "Partial Mask",
            description:
              "Replace PII but keep the last few characters (PCI-style, e.g. ****-****-****-0123).",
          },
          warn: {
            label: "Warn",
            description:
              "Forward the request as-is and record an incident in the audit log. Monitor mode.",
          },
          allow: {
            label: "Allow",
            description:
              "Forward as-is, no incident recorded. Only use when PII is expected (e.g. an internal KYC tool).",
          },
        },
      },
      injection: {
        title: "Prompt injection",
        description:
          "What the gateway does when the prompt-injection classifier scores above the threshold.",
        thresholdLabel: "Confidence threshold",
        thresholdHint:
          "Range 0.00 – 1.00. Lower = more sensitive (more flags). Default 0.70.",
        thresholdError: "Enter a number between 0.00 and 1.00.",
        options: {
          block: {
            label: "Block",
            description:
              "Refuse the request when the classifier crosses the threshold. Strictest.",
          },
          warn: {
            label: "Warn (default)",
            description:
              "Forward but record an incident — surfaced in observability.",
          },
          allow: {
            label: "Allow",
            description:
              "Skip enforcement entirely. Detector still runs; outcomes are not blocked.",
          },
        },
      },
      badges: {
        aria: "Current enforcement mode",
        enforced: "Enforced",
        masking: "Masking",
        monitoring: "Monitoring",
        off: "Off",
      },
    },
    users: {
      readOnlyNotice:
        "User management is only available to organisation admins. You can still view the member list below.",
      list: {
        title: "Organisation members",
        descriptionManage:
          "List of active users. Role management and account suspension are not yet available in the interface — use the admin API or contact the Monago team.",
        descriptionReadOnly:
          "List of active users in this organisation.",
        filterStatusLabel: "Status",
        filterRoleLabel: "Role",
        colName: "Name",
        colEmail: "Email",
        colRole: "Role",
        colStatus: "Status",
        colLastLogin: "Last login",
        emptyFiltered: "No members match the filter yet.",
        emptyValue: "—",
        neverLoggedIn: "Never",
      },
      invites: {
        title: "Active invitations",
        description:
          "Invitation codes that have not been used and not been revoked. Share a code with a prospective team member — each code can be used only once.",
        inviteButton: "Invite member",
        quotaUsage:
          "Using {active} of {max} pilot invitations.",
        quotaRemaining: " {remaining} slots remaining.",
        quotaReachedBadge: "Limit reached",
        quotaReachedAlert:
          "The pilot invitation limit has been reached. Revoke unused invitations or wait for them to expire to add new members.",
        colPrefix: "Code prefix",
        colRole: "Role",
        colNote: "Note",
        colExpires: "Valid until",
        colActions: "Actions",
        emptyValue: "—",
        emptyList:
          "No active invitations yet. Click \"Invite member\" to create a new invitation code.",
        revokeButton: "Revoke",
        revokeAria: "Revoke invitation code {prefix}",
      },
    },
  },
  dashboard: {
    greeting: {
      morning: "Good morning",
      afternoon: "Good afternoon",
      evening: "Good evening",
      night: "Good evening",
    },
    quickActions: {
      comingSoon: "Available in an upcoming release",
      createKeyLabel: "Create API key",
      createKeyDescription: "For inference and governance integration",
      observabilityLabel: "View observability",
      observabilityDescription: "Latency, token usage, and audit integrity",
      docsLabel: "Read the documentation",
      docsDescription: "API reference and governance guide",
    },
    governance: {
      label: "Governance posture",
      noData: "No data yet",
      bandExcellent: "Excellent",
      bandGood: "Good",
      bandNeedsAttention: "Needs attention",
      bandCritical: "Requires immediate review",
      bandHealthy: "Healthy",
      bandApproachingThreshold: "Approaching threshold",
    },
    workspaces: {
      label: "Active workspaces",
      none: "No active workspaces",
      single: "1 workspace",
      multiple: "{count} workspaces",
    },
    requests: {
      label: "Requests · 24h",
      caption: "Total inference in the last 24 hours",
    },
    policyDecisions: {
      label: "Policy decisions · 24h",
      caption: "Allow, deny, approval, and manual review combined",
    },
    riskScore: {
      label: "Risk score",
      noData: "No data yet",
      bandExcellent: "Excellent",
      bandGood: "Good",
      bandNeedsAttention: "Needs attention",
      bandCritical: "Requires immediate review",
    },
    recentActivity: {
      label: "Recent activity",
      empty: "No activity in the last 24 hours.",
    },
  },
  apiKeys: {
    page: {
      title: "API keys",
      description:
        "Manage API keys for integrating your systems with Monago. The secret key is shown only once after it is created.",
      createButton: "Create API key",
    },
    access: {
      sectionTitle: "How to access the API",
      sectionDescription:
        "Gateway endpoint for developer applications. Replace YOUR_MONAGO_KEY with a secret key from the table below.",
      baseUrlLabel: "Gateway base URL",
      baseUrlNote:
        "Production environments use a different URL — contact the Monago team for the final endpoint.",
      exampleOpenai: "Example request — OpenAI",
      exampleAnthropic: "Example request — Anthropic",
      tipLabel: "Tip:",
      tipPrefix: "Configure your AI provider credentials in ",
      tipLinkLabel: "AI providers",
      tipSuffix:
        " first. Without BYOK credentials, the gateway can use the built-in platform key when available; otherwise the request is rejected.",
    },
    quickstart: {
      title: "No API keys yet",
      description:
        "An API key is the credential for integrating your systems with Monago. Follow the three steps below to get started.",
      step1Title: "Create an API key",
      step1Body:
        "Give it a descriptive name, for example \"Production backend\", and set the rate limit to suit your needs.",
      step1Button: "Create API key",
      step2Title: "Test with curl",
      step2Body:
        "Once the key is created, replace the placeholder and run the command below to validate the integration:",
      step3Title: "Integrate into your systems",
      step3Body:
        "Store the API key in an environment variable or secret vault — do not hard-code it in your application. Monitor its usage on the Observability tab.",
    },
    filter: {
      all: "All",
      active: "Active",
      disabled: "Disabled",
      revoked: "Revoked",
      expired: "Expired",
    },
    status: {
      active: "Active",
      disabled: "Disabled",
      revoked: "Revoked",
      expired: "Expired",
    },
    table: {
      name: "Name",
      key: "Key",
      status: "Status",
      rateLimit: "Rate limit",
      lastUsed: "Last used",
      actions: "Actions",
      neverUsed: "Never used",
      emptyFiltered: "No keys match the current filter.",
      ratePerMinute: "{count}/min",
    },
    actions: {
      rowAriaLabel: "Actions for {name}",
      edit: "Edit name or description",
      enable: "Re-enable",
      enabling: "Enabling...",
      disable: "Disable",
      revoke: "Revoke permanently",
    },
    createDialog: {
      title: "Create a new API key",
      description:
        "The secret key is shown only once after it is created — store it securely before closing the dialog.",
      nameLabel: "Name",
      namePlaceholder: "e.g. Production backend",
      descriptionLabel: "Description (optional)",
      descriptionPlaceholder: "Note for the integration team",
      rateLimitLabel: "Rate limit (per minute)",
      rateLimitHint: "Common range: 60–1000 per minute for production.",
      expiryToggle: "Set an expiry date",
      expiryUnit: "days from now",
      cancel: "Cancel",
      submit: "Create key",
      submitting: "Creating...",
    },
    editDialog: {
      title: "Edit API key",
      description:
        "Only the name and description can be changed. To change the rate limit or workspace, revoke this key and create a new one.",
      nameLabel: "Name",
      descriptionLabel: "Description",
      descriptionPlaceholder: "Internal note (optional)",
      cancel: "Cancel",
      submit: "Save",
      submitting: "Saving...",
    },
    disableDialog: {
      title: "Disable API key",
      description:
        "The key {name} will be rejected at authentication until it is re-enabled. This action can be reversed.",
      reasonLabel: "Reason (optional)",
      reasonPlaceholder: "e.g. scheduled rotation for Q2",
      reasonHint: "The reason is recorded in the audit log for traceability.",
      cancel: "Cancel",
      submit: "Disable",
      submitting: "Processing...",
    },
    revokeDialog: {
      title: "Revoke API key",
      description:
        "This action cannot be undone. Once revoked, systems still using this key will fail authentication until they are updated with a new key.",
      confirmPrompt: "To confirm, type the key name exactly as shown: ",
      confirmLabel: "Confirm name",
      confirmMismatch: "Name does not match",
      reasonLabel: "Reason (optional)",
      reasonPlaceholder: "e.g. suspected key leak",
      cancel: "Cancel",
      submit: "Revoke permanently",
      submitting: "Revoking...",
    },
    showOnceDialog: {
      title: "API key created",
      description:
        "Save this key now. Once the dialog is closed, the key will not be shown again.",
      copy: "Copy",
      copied: "Copied",
      warningLabel: "Important:",
      warningBody:
        "This secret key will not be shown again. Store it in a password manager or secret vault before closing the dialog.",
      rateLimitLabel: "Rate limit:",
      rateLimitValue: "{count}/min",
      expiresLabel: "Expires:",
      acknowledge: "I have saved the secret key in a secure location.",
      close: "Saved, close",
    },
  },
  playground: {
    page: {
      title: "Playground",
      description:
        "Test prompts against your connected models with the full governance pipeline — PII redaction, policy enforcement, and an audit trail — applied to every call.",
      noSessionSelected: "No session selected",
      startSession: "Start a session",
    },
    sessions: {
      title: "Sessions",
      newButtonLabel: "New session (⌘N)",
      searchPlaceholder: "Search sessions",
      searchAriaLabel: "Search sessions",
      copyIdLabel: "Copy session id",
      deleteAriaLabel: "Delete session",
      confirmDelete: "Delete this session?",
      buckets: {
        today: "Today",
        yesterday: "Yesterday",
        earlier: "Earlier",
      },
      empty: {
        title: "No sessions yet.",
        cta: "Start your first session",
      },
      pills: {
        deny: "DENY",
        pii: "PII",
      },
      time: {
        justNow: "just now",
        minutesAgo: "{n}m ago",
        hoursAgo: "{n}h ago",
        daysAgo: "{n}d ago",
      },
    },
    chat: {
      compose: {
        placeholder: "Continue the conversation…",
        ariaLabel: "Compose message",
        disclaimer: "PII auto-redacted, policies enforced before send.",
        send: "Send via Gateway",
        stop: "Stop",
      },
      skill: {
        freeform: "No skill",
        freeformLabel: "No skill (freeform)",
        activeTitle: "Skill active — system prompt is locked",
        selectTitle: "Pick or manage a skill",
        manage: "Manage skills…",
      },
      meta: {
        msgs: "msgs {n}",
        tokens: "tokens {n}",
        flags: "flags {n}",
        modelSwitchTitle: "Switch model",
        modelNoProvidersTitle: "Connect a provider to choose models",
        modelPickPlaceholder: "Pick a model",
        modelSearchPlaceholder: "Search models…",
        modelNoResults: "No models match your search",
        modelPageInfo: "{a}–{b} of {n}",
        modelPrevPage: "Previous page",
        modelNextPage: "Next page",
        pricePerM: "≈ Rp {in} in · Rp {out} out / 1M tokens",
        noPrice: "estimate not yet available",
        noPriceHint:
          "We don't have a price estimate for this model yet — it's still fully usable, and the real cost is recorded after each call.",
        priceEstimateNote:
          "Estimate per 1M tokens at the current FX rate — not a per-request cost.",
      },
      system: {
        eyebrow: "SYSTEM",
        label: "System prompt",
        locked: "locked by workspace",
        empty: "No system prompt — click to add.",
        skillPick: "Use a skill",
        skillChange: "Change skill",
        skillNone: "No skills in this workspace yet. Create one on the Skills page.",
      },
      empty: {
        title: "Send a message to start.",
        body: "Pre-flight governance runs on every turn — PII detection, policy, routing, provider call, and audit.",
      },
      user: {
        label: "you",
        piiRedacted: "{n} PII redacted",
      },
      assistant: {
        brand: "Monago",
        thinking: "thinking…",
        policyBlocked: "Request blocked by policy",
        policyFlag: "policy flag",
        auditLink: "audit:{id}…",
        copy: "Copy",
        copied: "Copied",
      },
      noCredential: {
        title: "Connect a provider to use the playground",
        body: "The playground only uses your own provider credentials — it never falls back to a shared key. Add an OpenAI or Anthropic key in Providers, then come back here.",
        cta: "Open Providers",
      },
    },
    governance: {
      panelTitle: "Governance pipeline",
      expandAriaLabel: "Expand governance panel",
      collapseAriaLabel: "Collapse governance panel",
      awaitingTrace: "awaiting trace…",
      totalMs: "{ms} ms total",
      flaggedCount: "{n} flagged",
      railLabel: "Governance",
      railLabelFlagged: "Governance · {n} flagged",
      stepLabels: {
        policy: "Policy Check",
        pii: "PII Detection",
        routing: "Routing",
        provider: "Provider Call",
        audit: "Audit Log",
      },
      statusPills: {
        ok: "ok",
        running: "running",
        flagged: "flagged",
        blocked: "blocked",
        pending: "pending",
      },
      summary: {
        latency: "LATENCY",
        tokens: "TOKENS",
        cost: "COST",
        audit: "AUDIT",
        viewAuditTrail: "View full audit trail ↗",
        awaitingAuditId: "Awaiting audit id…",
      },
      firstToken: "First token",
      streaming: "Streaming",
      total: "Total",
    },
  },
  skills: {
    page: {
      title: "Skills",
      description: "Skills are specialist configurations (system prompt + parameters) with immutable versions, audit-linked, and evaluable.",
      newButton: "New skill",
      fromTemplate: "Create from template",
      loading: "Loading skills…",
      confirmDelete: "Delete this skill? Past audit rows keep their reference.",
      expand: "Expand version history",
      collapse: "Collapse version history",
      activeTag: "active",
      activeVersionTitle: "Current version v{v}",
      versionCount: "{n} versions total",
      lastUpdated: "updated {when}",
      evalLink: "Evaluation status",
      tryInPlayground: "Try in Playground",
      editButton: "Edit",
      deleteButton: "Delete",
      versionHistory: "Version history",
      versionsLoading: "Loading version history…",
      versionsEmpty: "No versions stored yet.",
      promoteButton: "Set as active",
      emptyTitle: "No skills in this workspace yet",
      emptyBody: "Start from one of the AI governance templates below, or create from scratch. Skills can be used in the Playground and tested on the Evaluation page.",
      emptyCreate: "Start blank",
    },
    form: {
      name: "Name",
      slug: "Slug",
      slugHint: "Optional. Auto-derived from the name when left blank.",
      slugPlaceholder: "e.g. ai-governance-policy-advisor",
      description: "Description",
      systemPrompt: "System prompt",
      systemPromptTemplateHint: "Template content has been pre-filled. Adjust as needed, then save as v1.",
      model: "Preferred model",
      modelNoPreference: "No preference (use session model)",
      modelInactive: "{model} (inactive)",
      modelInactiveHint: "The provider for this model is no longer active. Pick another model, or keep this as no preference.",
      modelNoProviders: "No active AI provider yet.",
      modelNoProvidersCta: "Add a provider →",
      temperature: "Temperature",
      notes: "Version notes",
      cancel: "Cancel",
      close: "Close",
      saveNew: "Create skill",
      saving: "Saving…",
      blankDescription: "Create a brand-new skill from scratch.",
      templateDescription: "This form has been pre-filled from a template. Edit as needed, then save as v1.",
    },
    editDialog: {
      title: "Edit skill",
      description: "Update name/description, or append a new version. Older versions are kept; promote one from the version history.",
      metadataHeading: "Skill metadata",
      saveMetadata: "Save metadata",
      savingMetadata: "Saving…",
      newVersionHeading: "New version (becomes v{v})",
      createVersion: "Create new version",
      creatingVersion: "Creating…",
      notesPlaceholder: "What changed in this version?",
      versionsNote: "New versions don't auto-promote — promote from the skill card on the Skills page.",
    },
  },
  evaluation: {
    page: {
      title: "Evaluation",
      description: "Test a skill with governance scenarios — inputs run through the real pipeline and we assert on the actual governance trace.",
      costNotice: "Each run sends real requests through the gateway (BYOK). k attempts = k provider calls. pass^k means every attempt must pass; pass@k means at least one.",
      skillsLoading: "Loading skills…",
      noSkills: "No skills in this workspace yet. Create one in the playground first.",
    },
    picker: {
      skill: "Skill",
      version: "Version to test",
      versionHint: "Active version: v{v}.",
      refreshSkills: "Refresh skills",
    },
    scenarios: {
      heading: "{n} scenarios",
      newButton: "New scenario",
      editButton: "Edit",
      deleteButton: "Delete",
      confirmDelete: "Delete this scenario?",
      loading: "Loading scenarios…",
      emptyTitle: "No scenarios yet for this skill",
      emptyBody: "Author governance scenarios — inputs that should trigger PII redaction, a policy deny, or a prompt-injection flag.",
      noRunsYet: "No runs yet for this version.",
      expand: "Expand details",
      collapse: "Collapse details",
    },
    category: {
      pii: "PII",
      policy: "POLICY",
      security: "SECURITY",
    },
    run: {
      runButton: "Run",
      running: "Running…",
      batchButton: "Run all",
      batchRunning: "Running…",
      confirmSingle: "Run this scenario {n} attempts (= {n} provider calls, BYOK)?",
      confirmBatch: "Run {scenarios} scenarios totalling {n} attempts? All are real provider calls.",
    },
    result: {
      never: "Never run",
      title: "{result} · {passes}/{k} attempts passed",
    },
    evidence: {
      heading: "Latest run · v{version} · {when}",
      attemptPass: "Attempt {n} — PASS",
      attemptFail: "Attempt {n} — FAIL",
    },
    history: {
      heading: "Run history",
      loading: "Loading history…",
      empty: "No run history for this skill yet.",
    },
    summary: {
      piiFlagged: "expects PII detected",
      piiClean: "expects no PII",
      piiTypes: "PII types include {types}",
      policyBlocked: "expects policy block",
      securityFlagged: "expects security flag (e.g. prompt injection)",
      noContent: "expects no content emitted",
      empty: "No assertions configured.",
    },
    form: {
      createTitle: "New scenario",
      editTitle: "Edit scenario",
      description: "A scenario sends an input through the skill's real pipeline and asserts on the resulting governance trace.",
      name: "Name",
      category: "Category",
      inputMessage: "User message",
      expectHeading: "What must happen (expect)",
      expectPiiFlagged: "PII detected",
      expectAny: "any",
      expectYes: "yes",
      expectNo: "no",
      expectPiiTypes: "Required PII types (optional)",
      expectPiiTypesHint: "Comma-separated; case-insensitive. Example: NIK, EMAIL.",
      expectPolicyBlocked: "Policy must block",
      expectSecurityFlagged: "Security flag (e.g. prompt injection)",
      expectNoContent: "No content may stream (hard block)",
      metric: "Metric",
      metricCaret: "pass^k (every attempt passes)",
      metricAt: "pass@k (at least one passes)",
      k: "k",
      kHint: "Min 1, max 10. k = attempts = provider calls per run.",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving…",
      errorEmptyExpect: "At least one assertion must be selected.",
    },
  },
  errors: {
    notFound: {
      title: "Page not found",
      code: "404",
      description:
        "The page you are looking for is not available. The link may have changed or been removed.",
      backHome: "Back to dashboard",
    },
  },
  shared: {
    skipToContent: "Skip to main content",
    pagination: {
      rangeLabel: "Showing rows {start}–{end}",
      previous: "Previous",
      next: "Next",
    },
    errorPanel: {
      defaultTitle: "Failed to load data",
    },
    passwordInput: {
      show: "Show password",
      hide: "Hide password",
    },
    loading: {
      default: "Loading",
    },
    breadcrumb: {
      ariaLabel: "Breadcrumb",
    },
    jsonEditor: {
      valid: "Valid JSON",
      invalid: "Invalid JSON",
      prompt: "Enter JSON",
      format: "Format",
    },
    topbar: {
      openMenu: "Open menu",
    },
  },

  platform: {
    tenants: {
      page: {
        title: "Tenants",
        description:
          "Provision sandbox / POC tenants, monitor trial deadlines, and extend or convert active trials. Super-admin only.",
        createButton: "Provision tenant",
      },
      list: {
        title: "All tenants",
        description:
          "Cross-tenant view. Trial countdown derives from `trial_expires_at`; tenants without a deadline are unlimited (paid / internal).",
        colName: "Name",
        colSlug: "Slug",
        colTrialStatus: "Trial",
        colCreated: "Created",
        colActions: "Actions",
        empty: "No tenants yet. Use Provision tenant to add the first one.",
        statusUnlimited: "Unlimited",
        statusTrial: "Trial · {days} days left",
        statusExpired: "Expired {days} day(s) ago",
        extendButton: "Extend trial",
        extendUnavailable: "Unlimited tenant — extend not applicable.",
      },
      create: {
        title: "Provision tenant",
        description:
          "Creates the tenant + first admin + default workspace in one call. A temporary password is generated and shown once.",
        nameLabel: "Tenant name",
        slugLabel: "Slug",
        slugHint: "Lowercase letters, digits, hyphens. Auto-derived from the name.",
        regionLabel: "Region code",
        regionHint: "e.g. id, sg, my, th, ph.",
        trialDaysLabel: "Trial days",
        trialDaysHint: "Between 1 and 365. Default 45.",
        adminEmailLabel: "Admin email",
        adminNameLabel: "Admin full name",
        passwordHint:
          "A 20-character temporary password is generated automatically. You'll see it once on the next screen — copy it before closing.",
        cancel: "Cancel",
        submit: "Provision",
        submitLoading: "Provisioning...",
      },
      provisionResult: {
        title: "Tenant provisioned",
        description:
          "Hand off these credentials to the {tenant} admin. The temporary password is shown only once.",
        emailLabel: "Admin email",
        passwordLabel: "Temporary password",
        copy: "Copy",
        copied: "Copied",
        warningHeading: "Important:",
        warningBody:
          "This password is shown only once. Monago does not store it in plaintext and cannot retrieve it again. The admin must change it on first login.",
        trialEndsLabel: "Trial ends",
        slugLabel: "Tenant slug",
        acknowledge: "I have copied the credentials and saved them somewhere safe.",
        closeButton: "Saved, close",
      },
      extend: {
        title: "Extend trial",
        description: "Add days to {tenant}'s trial deadline.",
        daysLabel: "Extra days",
        reasonLabel: "Reason (optional)",
        reasonHint: "Audit metadata — explains the extension for later review.",
        cancel: "Cancel",
        submit: "Extend",
        submitLoading: "Extending...",
      },
    },
  },
};
